import { useState, useEffect, useRef } from 'react'
import { ArrowLeft, Copy, Check, Clock, FileText, ChevronDown, AlertCircle, Info, Loader } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { getTransactions } from '../../services/walletService'
import { TxDetailScreen } from './History'
import { useToast } from '../../context/ToastContext'
import { useAuth } from '../../context/AuthContext'
import Layout from '../../components/Layout/Layout'
import { formatAmount, formatSignedAmount } from '../../lib/utils'
import '../../styles/dashboard.css'

const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL
  ? `${(import.meta as any).env.VITE_API_BASE_URL}/api`
  : ((import.meta as any).env?.VITE_API_URL
    ? `${(import.meta as any).env.VITE_API_URL}/api`
    : 'http://localhost:5000/api')

const ASSETS = [
  { symbol: 'USDT', name: 'Tether USD', icon: 'https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons@master/32/color/usdt.png' },
  { symbol: 'BTC',  name: 'Bitcoin',    icon: 'https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons@master/32/color/btc.png'  },
  { symbol: 'ETH',  name: 'Ethereum',   icon: 'https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons@master/32/color/eth.png'  },
]

interface Network { name: string; tag: string; time: string; key: string }
const NETWORKS: Record<string, Network[]> = {
  USDT: [
    { name: 'Tron',      tag: 'TRC20', time: '~1 min',   key: 'trc20_TRC20' },
    { name: 'BNB Chain', tag: 'BEP20', time: '~2 mins',  key: 'evm_BEP20'   },
    { name: 'Ethereum',  tag: 'ERC20', time: '~5 mins',  key: 'evm_ERC20'   },
  ],
  BTC:  [{ name: 'Bitcoin',  tag: 'BTC',   time: '~10 mins', key: 'btc_BTC'   }],
  ETH:  [{ name: 'Ethereum', tag: 'ERC20', time: '~5 mins',  key: 'evm_ERC20' }],
}

export default function DepositCrypto() {
  const navigate    = useNavigate()
  const { toast }   = useToast()
  const { user }    = useAuth()

  const [showHistory,     setShowHistory]     = useState(false)
  // Binance-style: tapping "Deposit" anywhere in the app lands here first with a
  // chooser between on-chain deposit and P2P (buy crypto from another user) —
  // P2P itself is no longer exposed directly in the nav/menus (see issue #7).
  const [showChooser,     setShowChooser]     = useState(true)
  const [historyTab,      setHistoryTab]       = useState<'deposit' | 'withdrawal' | 'other'>('deposit')
  const [transactions,    setTransactions]     = useState<any[]>([])
  const [selectedTx,      setSelectedTx]        = useState<any | null>(null)
  const [copied,          setCopied]           = useState(false)
  const [selectedAsset,   setSelectedAsset]    = useState(ASSETS[0])
  const [selectedNetwork, setSelectedNetwork]  = useState(NETWORKS['USDT'][0])
  const [openAsset,       setOpenAsset]        = useState(false)
  const [openNetwork,     setOpenNetwork]      = useState(false)
  const [apiCoins,        setApiCoins]         = useState<any[]>([])

  // Address state
  const [walletAddress,   setWalletAddress]    = useState<string>('')
  const [addressLoading,  setAddressLoading]   = useState(false)
  const [addressError,    setAddressError]     = useState<string>('')

  const containerRef = useRef<HTMLDivElement>(null)

  // Load coin config
  useEffect(() => {
    fetch(`${API_BASE}/users/coins`)
      .then(r => r.json())
      .then(d => { if (d.data?.length) setApiCoins(d.data) })
      .catch(() => {})
  }, [])

  const fetchTxns = () => getTransactions().then(setTransactions).catch(() => {})
  useEffect(() => { fetchTxns() }, [])

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpenAsset(false); setOpenNetwork(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Load address whenever asset or network changes
  useEffect(() => {
    loadAddress()
  }, [selectedAsset.symbol, selectedNetwork.tag])

  const loadAddress = async () => {
    setAddressLoading(true)
    setAddressError('')
    setWalletAddress('')
    try {
      const token = (() => {
        try { return JSON.parse(localStorage.getItem('userInfo') || '{}').token } catch { return null }
      })()
      const res = await fetch(`${API_BASE}/wallet/create-deposit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ currency: selectedAsset.symbol, network: selectedNetwork.tag }),
      })
      const data = await res.json()
      if (!res.ok || !data.walletAddress) {
        throw new Error(data.message || 'Failed to load deposit address')
      }
      setWalletAddress(data.walletAddress)
    } catch (err: any) {
      setAddressError(err.message || 'Failed to load address')
    } finally {
      setAddressLoading(false)
    }
  }

  const visibleAssets = apiCoins.length
    ? ASSETS.filter(a => {
        const c = apiCoins.find((x: any) => x.symbol === a.symbol)
        return !c || (c.isActive && c.depositEnabled !== false)
      })
    : ASSETS

  const visibleNets = (() => {
    const base = NETWORKS[selectedAsset.symbol] || NETWORKS['USDT']
    if (!apiCoins.length) return base
    const cfg = apiCoins.find((c: any) => c.symbol === selectedAsset.symbol)
    if (!cfg?.networks?.length) return base
    return base.filter(n => {
      const nc = cfg.networks.find((x: any) => x?.network && x.network.toUpperCase() === n.tag.toUpperCase())
      return !nc || nc.depositEnabled !== false
    })
  })()

  const networkCoinConfig = (() => {
    const cfg = apiCoins.find((c: any) => c.symbol === selectedAsset.symbol)
    if (!cfg?.networks?.length) return null
    return cfg.networks.find((n: any) => n?.network && n.network.toUpperCase() === selectedNetwork.tag.toUpperCase()) || null
  })()
  const minDeposit    = networkCoinConfig?.minDeposit            ?? (selectedAsset.symbol === 'BTC' ? 0.0001 : 1)
  const confirmations = networkCoinConfig?.confirmationsRequired ?? (selectedAsset.symbol === 'BTC' ? 3 : 1)

  const copy = () => {
    if (!walletAddress) return
    navigator.clipboard.writeText(walletAddress)
    setCopied(true)
    toast('Address copied!', 'info')
    setTimeout(() => setCopied(false), 2000)
  }

  const pickAsset = (a: typeof ASSETS[0]) => {
    setSelectedAsset(a)
    setOpenAsset(false)
    setSelectedNetwork((NETWORKS[a.symbol] || NETWORKS['USDT'])[0])
  }

  const historyItems = transactions.filter(t => {
    if (historyTab === 'deposit')    return t.type === 'deposit' || t.type === 'adjustment'
    if (historyTab === 'withdrawal') return t.type === 'withdrawal'
    return !['deposit', 'withdrawal', 'adjustment'].includes(t.type)
  })

  const card: React.CSSProperties = {
    background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 16,
  }
  const dropBtn: React.CSSProperties = {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 12,
    padding: '12px 14px', cursor: 'pointer',
  }
  const dropMenu: React.CSSProperties = {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
    background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 12,
    overflow: 'hidden', zIndex: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  }
  const dropItem: React.CSSProperties = {
    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
    padding: '11px 14px', background: 'none', border: 'none',
    borderBottom: '1px solid var(--ui-border)', cursor: 'pointer',
  }

  // ── Deposit method chooser (Binance-style) ─────────────────────────────────────
  if (showChooser) {
    return (
      <Layout activePage="deposit" hideMobileNav hideFooterMobile>
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 9999,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }} onClick={() => navigate(-1)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderBottom: 'none',
            borderRadius: '20px 20px 0 0', padding: '20px 20px 32px', width: '100%', maxWidth: 480,
            color: 'var(--ui-text-primary)',
          }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--ui-border)', margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 4px' }}>Deposit</h3>
            <p style={{ fontSize: 13, color: 'var(--ui-text-muted)', margin: '0 0 20px' }}>Choose how you'd like to add crypto to your account.</p>

            <button onClick={() => setShowChooser(false)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
              background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 14,
              padding: '16px', marginBottom: 12, cursor: 'pointer', color: 'inherit',
            }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(28,212,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1CD4A7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20"/><path d="m17 7-5-5-5 5"/><rect x="3" y="17" width="18" height="4" rx="1"/></svg>
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>On-chain Deposit</div>
                <div style={{ fontSize: 12, color: 'var(--ui-text-muted)', marginTop: 2 }}>Send crypto from another wallet or exchange</div>
              </div>
            </button>

            <button onClick={() => navigate('/p2p')} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
              background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 14,
              padding: '16px', cursor: 'pointer', color: 'inherit',
            }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(240,185,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F0B90B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>P2P</div>
                <div style={{ fontSize: 12, color: 'var(--ui-text-muted)', marginTop: 2 }}>Buy crypto directly from other Bicoin users</div>
              </div>
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  // ── History view ─────────────────────────────────────────────────────────────
  if (showHistory) {
    return (
      <Layout activePage="deposit" hideMobileNav hideFooterMobile>
        <div style={{ maxWidth: 560, margin: '0 auto', padding: '20px 16px 48px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
            <button onClick={() => setShowHistory(false)}
              style={{ background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 10, padding: '8px 10px', cursor: 'pointer', color: 'var(--ui-text-primary)', display: 'flex' }}>
              <ArrowLeft size={18} />
            </button>
            <span style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 18 }}>Transaction History</span>
          </div>
          <div style={{ display: 'flex', background: 'var(--ui-bg-elevated)', borderRadius: 12, padding: 4, marginBottom: 20 }}>
            {(['deposit', 'withdrawal', 'other'] as const).map(t => (
              <button key={t} onClick={() => setHistoryTab(t)}
                style={{ flex: 1, padding: '8px 0', borderRadius: 9, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                  background: historyTab === t ? '#0d9488' : 'transparent',
                  color: historyTab === t ? '#fff' : 'var(--ui-text-muted)' }}>
                {t[0].toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          {historyItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--ui-text-muted)' }}>
              <Clock size={36} style={{ margin: '0 auto 10px', display: 'block', opacity: .4 }} />
              <p style={{ margin: 0, fontSize: 14 }}>No records yet</p>
            </div>
          ) : historyItems.map(item => (
            <div key={item._id} onClick={() => setSelectedTx(item)} style={{ ...card, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#052e26', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Check size={15} color="#10b981" />
                </div>
                <div>
                  <div style={{ color: 'var(--ui-text-primary)', fontWeight: 600, fontSize: 14 }}>{item.asset}</div>
                  <div style={{ color: 'var(--ui-text-muted)', fontSize: 12 }}>{new Date(item.createdAt).toLocaleString()}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: item.type === 'withdrawal' ? '#EF4444' : '#10b981', fontWeight: 700, fontSize: 15 }}>
                  {item.type === 'withdrawal' ? '-' : '+'}{formatAmount(item.amount)}
                </div>
                <span style={{ display: 'inline-block', marginTop: 4, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  background: item.status === 'completed' ? '#052e26' : '#1c1408',
                  color: item.status === 'completed' ? '#10b981' : '#f59e0b' }}>
                  {item.status}
                </span>
              </div>
            </div>
          ))}
          {selectedTx && <TxDetailScreen tx={selectedTx} onBack={() => setSelectedTx(null)} />}
        </div>
      </Layout>
    )
  }

  // ── Main view ─────────────────────────────────────────────────────────────────
  return (
    <Layout activePage="deposit" hideMobileNav hideFooterMobile>
      <div ref={containerRef} style={{ maxWidth: 560, margin: '0 auto', padding: '16px 16px 60px' }}>

        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <button onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ui-text-secondary)', display: 'flex', alignItems: 'center', gap: 6, padding: 0, fontSize: 14 }}>
            <ArrowLeft size={20} /><span>Back</span>
          </button>
          <span style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 18 }}>Deposit</span>
          <button onClick={() => setShowHistory(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ui-text-muted)', display: 'flex', padding: 4 }}>
            <FileText size={20} />
          </button>
        </div>

        {/* Asset + Network row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          {/* Asset */}
          <div>
            <div style={{ fontSize: 11, color: 'var(--ui-text-muted)', fontWeight: 600, letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 6 }}>Asset</div>
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setOpenAsset(!openAsset); setOpenNetwork(false) }} style={dropBtn}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <img src={selectedAsset.icon} style={{ width: 24, height: 24, borderRadius: '50%' }} alt={selectedAsset.symbol} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>{selectedAsset.symbol}</div>
                    <div style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>{selectedAsset.name}</div>
                  </div>
                </div>
                <ChevronDown size={16} color="var(--ui-text-muted)" style={{ transform: openAsset ? 'rotate(180deg)' : 'none', transition: '.2s', flexShrink: 0 }} />
              </button>
              {openAsset && (
                <div style={dropMenu}>
                  {visibleAssets.map(a => (
                    <button key={a.symbol} onClick={() => pickAsset(a)} style={{ ...dropItem, background: selectedAsset.symbol === a.symbol ? 'var(--ui-bg-primary)' : 'none' }}>
                      <img src={a.icon} style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0 }} alt={a.symbol} />
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <div style={{ color: 'var(--ui-text-primary)', fontWeight: 600, fontSize: 13 }}>{a.symbol}</div>
                        <div style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>{a.name}</div>
                      </div>
                      {selectedAsset.symbol === a.symbol && <Check size={14} color="#0d9488" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Network */}
          <div>
            <div style={{ fontSize: 11, color: 'var(--ui-text-muted)', fontWeight: 600, letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 6 }}>Network</div>
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setOpenNetwork(!openNetwork); setOpenAsset(false) }} style={dropBtn}>
                <div style={{ textAlign: 'left', overflow: 'hidden' }}>
                  <div style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 14, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedNetwork.name}</div>
                  <div style={{ color: '#0d9488', fontSize: 11 }}>{selectedNetwork.tag} · {selectedNetwork.time}</div>
                </div>
                <ChevronDown size={16} color="var(--ui-text-muted)" style={{ transform: openNetwork ? 'rotate(180deg)' : 'none', transition: '.2s', flexShrink: 0 }} />
              </button>
              {openNetwork && (
                <div style={dropMenu}>
                  {visibleNets.map(n => (
                    <button key={n.key} onClick={() => { setSelectedNetwork(n); setOpenNetwork(false) }} style={{ ...dropItem, background: selectedNetwork.key === n.key ? 'var(--ui-bg-primary)' : 'none' }}>
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <div style={{ color: 'var(--ui-text-primary)', fontSize: 13, fontWeight: 600 }}>{n.name} <span style={{ color: 'var(--ui-text-muted)', fontWeight: 400 }}>({n.tag})</span></div>
                        <div style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>{n.time}</div>
                      </div>
                      {selectedNetwork.key === n.key && <Check size={14} color="#0d9488" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* QR + Address card */}
        <div style={{ ...card, padding: '24px 20px', marginBottom: 16 }}>

          {/* QR */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <div style={{ background: '#fff', padding: 10, borderRadius: 14, width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {addressLoading ? (
                <Loader size={32} color="#0d9488" style={{ animation: 'spin 1s linear infinite' }} />
              ) : walletAddress ? (
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(walletAddress)}&bgcolor=ffffff&color=000000`}
                  alt="QR"
                  style={{ width: 180, height: 180, display: 'block' }}
                />
              ) : (
                <div style={{ color: 'var(--ui-text-muted)', fontSize: 12, textAlign: 'center', padding: 8 }}>No address</div>
              )}
            </div>
          </div>

          {/* Network badge */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <span style={{ background: 'rgba(13,148,136,0.1)', border: '1px solid rgba(13,148,136,0.3)', borderRadius: 20, padding: '4px 14px', fontSize: 12, color: '#0d9488', fontWeight: 600 }}>
              {selectedNetwork.name} · {selectedNetwork.tag}
            </span>
          </div>

          {/* Address label */}
          <div style={{ fontSize: 11, color: 'var(--ui-text-muted)', fontWeight: 600, letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 6 }}>
            {selectedNetwork.tag} Address
          </div>

          {/* Address row */}
          {addressLoading ? (
            <div style={{ background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 12, padding: '16px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
              <Loader size={16} color="#0d9488" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ color: 'var(--ui-text-muted)', fontSize: 13 }}>Generating your address…</span>
            </div>
          ) : addressError ? (
            <div style={{ background: '#1c0a0a', border: '1px solid #7f1d1d', borderRadius: 12, padding: '14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertCircle size={15} color="#ef4444" style={{ flexShrink: 0 }} />
              <span style={{ color: '#fca5a5', fontSize: 13, flex: 1 }}>{addressError}</span>
              <button onClick={loadAddress} style={{ background: '#7f1d1d', border: 'none', borderRadius: 6, padding: '5px 10px', color: '#fff', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
                Retry
              </button>
            </div>
          ) : (
            <div style={{ background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <span style={{
                flex: 1, color: 'var(--ui-text-primary)', fontSize: 13, fontFamily: 'monospace',
                wordBreak: 'break-all', overflowWrap: 'anywhere', lineHeight: 1.6, minWidth: 0,
              }}>
                {walletAddress || '—'}
              </span>
              <button onClick={copy} disabled={!walletAddress}
                style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5, background: copied ? '#0d9488' : 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 8, padding: '8px 12px', cursor: walletAddress ? 'pointer' : 'not-allowed', color: copied ? '#fff' : 'var(--ui-text-primary)', fontSize: 13, fontWeight: 600, transition: '.2s', whiteSpace: 'nowrap' }}>
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
          )}

          {/* Network info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: 'var(--ui-text-muted)', marginBottom: 3 }}>Minimum Deposit</div>
              <div style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 14 }}>
                {minDeposit} <span style={{ color: 'var(--ui-text-muted)', fontWeight: 400, fontSize: 12 }}>{selectedAsset.symbol}</span>
              </div>
            </div>
            <div style={{ background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: 'var(--ui-text-muted)', marginBottom: 3 }}>Expected Arrival</div>
              <div style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 14 }}>
                {confirmations} <span style={{ color: 'var(--ui-text-muted)', fontWeight: 400, fontSize: 12 }}>confirmation{confirmations !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Warning */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
          <AlertCircle size={15} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 12, color: '#fbbf24', lineHeight: 1.6 }}>
            Send only <strong>{selectedAsset.symbol}</strong> via <strong>{selectedNetwork.tag}</strong> to this address. Sending any other asset or using a different network will result in permanent loss.
          </span>
        </div>

        {/* Info */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 12, padding: '12px 14px', marginBottom: 24 }}>
          <Info size={15} color="#60a5fa" style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 12, color: '#93c5fd', lineHeight: 1.6 }}>
            This is your permanent deposit address. Your balance is credited automatically after on-chain confirmation — no action required.
          </span>
        </div>

        {/* Recent deposits */}
        <div style={{ ...card, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--ui-border)' }}>
            <span style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 15 }}>Recent Deposits</span>
            <button onClick={() => setShowHistory(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0d9488', fontSize: 13 }}>View all</button>
          </div>
          {transactions.filter(t => t.type === 'deposit' || t.type === 'adjustment').length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--ui-text-muted)', fontSize: 13 }}>No deposits yet</div>
          ) : (
            transactions.filter(t => t.type === 'deposit' || t.type === 'adjustment').slice(0, 5).map(tx => (
              <div key={tx._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--ui-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: tx.status === 'completed' ? '#052e26' : '#1c1408', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {tx.status === 'completed' ? <Check size={14} color="#10b981" /> : <Clock size={14} color="#f59e0b" />}
                  </div>
                  <div>
                    <div style={{ color: 'var(--ui-text-primary)', fontWeight: 600, fontSize: 14 }}>{tx.asset}</div>
                    <div style={{ color: 'var(--ui-text-muted)', fontSize: 12 }}>{new Date(tx.createdAt).toLocaleDateString()}</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#10b981', fontWeight: 700, fontSize: 14 }}>{formatSignedAmount(tx.amount)}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: tx.status === 'completed' ? '#10b981' : '#f59e0b' }}>{tx.status}</div>
                </div>
              </div>
            ))
          )}
        </div>

      </div>

      {/* Spinner keyframes */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Layout>
  )
}
